"use client";
import { createContext, ReactNode, useContext } from "react";

import { ActiveDirectoryApiContextType } from "@/types/helpDesk";

import { httpRequest } from "@/lib/http";
import { getErrorMessage } from "@/lib/data";

import { useAuth } from "@/context/util/auth";
import { useUi } from "@/context/util/ui";

const ActiveDirectoryApiContext = createContext<ActiveDirectoryApiContextType>(
  null!
);

export function ActiveDirectoryApiProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { setLoading, setSnackbar } = useUi();
  const { getAccessToken } = useAuth();

  async function setAccountStatus(
    username: string,
    action: "disable" | "unlock",
    reason: string
  ) {
    setLoading(true);

    try {
      const token = await getAccessToken();

      if (!token) throw new Error("No access token available");

      const url = `/account-utilities/api/help-desk/security/status?action=${action}`;

      const body = JSON.stringify({ username, reason });
      const headers = { Authorization: `Bearer ${token}` };
      const method = "PUT";

      const requestInit: RequestInit = { body, headers, method };

      const setAccountStatusResponse = (
        await httpRequest<{ message: string }>(url, requestInit)
      ).data;

      if (!setAccountStatusResponse) throw new Error("Empty http response");

      const { message } = setAccountStatusResponse;

      setSnackbar((prev) => ({
        ...prev,
        message,
        open: true,
        severity: "success",
      }));

      return true;
    } catch (error) {
      const message =
        "Failed to set user's account status. Please try again later.";
      console.error(message, error);

      setSnackbar((prev) => ({
        ...prev,
        message,
        open: true,
        severity: "error",
      }));
    } finally {
      setLoading(false);
    }
  }

  async function setPassword(username: string, password: string) {
    setLoading(true);

    try {
      const token = await getAccessToken();

      if (!token) throw new Error("No access token available");

      const url = "/account-utilities/api/help-desk/security/password";

      const body = JSON.stringify({ username, password });
      const headers = { Authorization: `Bearer ${token}` };
      const method = "PUT";

      const requestInit: RequestInit = { body, headers, method };

      const setPasswordResponse = (
        await httpRequest<{ message: string }>(url, requestInit)
      ).data;

      if (!setPasswordResponse) throw new Error("Empty http response");

      const { message } = setPasswordResponse;

      setSnackbar((prev) => ({
        ...prev,
        message,
        open: true,
        severity: "success",
      }));

      return true;
    } catch (error) {
      console.error(error);
      setSnackbar((prev) => ({
        ...prev,
        message: getErrorMessage(error),
        open: true,
        severity: "error",
      }));
    } finally {
      setLoading(false);
    }
  }

  return (
    <ActiveDirectoryApiContext.Provider
      value={{ setAccountStatus, setPassword }}
    >
      {children}
    </ActiveDirectoryApiContext.Provider>
  );
}

export function useActiveDirectoryApi() {
  const ctx = useContext(ActiveDirectoryApiContext);
  if (!ctx)
    throw new Error(
      "useActiveDirectoryApi must be used within a ActiveDirectoryApiContext"
    );
  return ctx;
}
